import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/lib/theme";
import api, { localApi } from "@/lib/api";
import { applyBrandColor } from "@/lib/brand";
import Announcements from "@/components/Announcements";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  SquaresFour,
  UsersThree,
  Clock,
  CalendarDots,
  Fingerprint,
  Money,
  ClipboardText,
  FileText,
  Bell,
  ChartBar,
  GearSix,
  SignOut,
  List,
  Buildings,
  CreditCard,
  HandCoins,
  Medal,
  Sun,
  Moon,
  BookOpen,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/", label: "Panel", icon: SquaresFour, end: true },
  { to: "/empleados", label: "Empleados", icon: UsersThree, admin: true },
  { to: "/turnos", label: "Turnos", icon: Clock, admin: true },
  { to: "/horarios", label: "Horarios", icon: CalendarDots, admin: true },
  { to: "/asistencia", label: "Asistencia", icon: Fingerprint },
  { to: "/planillas", label: "Planillas", icon: Money, admin: true },
  { to: "/prestamos", label: "Préstamos", icon: HandCoins, admin: true },
  { to: "/comisiones", label: "Comisiones", icon: Medal, admin: true },
  { to: "/solicitudes", label: "Solicitudes", icon: ClipboardText },
  { to: "/documentos", label: "Documentos", icon: FileText },
  { to: "/notificaciones", label: "Notificaciones", icon: Bell },
  { to: "/reportes", label: "Reportes", icon: ChartBar, admin: true },
  { to: "/mi-suscripcion", label: "Mi suscripción", icon: CreditCard, admin: true },
  { to: "/configuracion", label: "Configuración", icon: GearSix, admin: true },
  { to: "/manual", label: "Manual", icon: BookOpen },
];

function NavItems({ onNavigate }) {
  const { user } = useAuth();
  const items = NAV.filter((i) => !i.admin || user?.role === "admin");
  return (
    <nav className="flex flex-col gap-0.5 px-3" data-testid="main-nav">
      {items.map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onNavigate}
          data-testid={`nav-${label.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm border-l-2 transition-colors duration-200 ${
              isActive
                ? "border-primary bg-primary/10 text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground hover:bg-accent/60"
            }`
          }
        >
          <Icon className="h-4.5 w-4.5 shrink-0" size={18} />
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);

  useEffect(() => {
    api
      .get("/company")
      .then((r) => setCompany(r.data))
      .catch(() => {});
    localApi
      .post("/tenant/ping")
      .then((r) => {
        if (r.data?.active === false) {
          logout();
          navigate("/login");
          return;
        }
        const brand = r.data?.theme?.primary;
        if (brand) {
          applyBrandColor(brand);
          localStorage.setItem("caus_brand", JSON.stringify({ primary: brand }));
        }
      })
      .catch(() => {});
  }, []);

  return (
    <div className="flex h-full flex-col bg-background">
      <div className="px-5 pt-6 pb-5 border-b border-border">
        <div className="font-heading text-xl font-black tracking-tight text-foreground" data-testid="brand">
          CAUS<span className="text-muted-foreground">·</span>RRHH
        </div>
        <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
          <Buildings size={13} />
          <span className="truncate" data-testid="company-name">
            {company?.nombre_comercial || company?.razon_social || "Portal web"}
          </span>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto py-4">
        <NavItems onNavigate={onNavigate} />
      </div>
      <div className="border-t border-border p-4">
        <div className="text-sm text-foreground font-medium truncate" data-testid="user-name">{user?.full_name}</div>
        <div className="text-xs text-muted-foreground truncate">{user?.username}</div>
        <div className="mt-3 flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 rounded-sm border-input bg-transparent text-foreground/80 hover:bg-accent hover:text-accent-foreground"
            data-testid="logout-button"
            onClick={() => {
              logout();
              navigate("/login");
            }}
          >
            <SignOut className="mr-2" size={15} /> Cerrar sesión
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0 rounded-sm border-input bg-transparent text-foreground/80 hover:bg-accent hover:text-accent-foreground"
            data-testid="theme-toggle"
            onClick={toggle}
            title={theme === "dark" ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function Layout() {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-background text-foreground">
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col border-r border-border">
        <SidebarContent />
      </aside>

      <div className="lg:hidden sticky top-0 z-30 flex items-center justify-between border-b border-border bg-background px-4 py-3">
        <span className="font-heading text-lg font-black tracking-tight">
          CAUS<span className="text-muted-foreground">·</span>RRHH
        </span>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon" className="rounded-sm border-input bg-transparent" data-testid="mobile-menu-button">
              <List size={20} />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 border-r border-border bg-background p-0">
            <SidebarContent onNavigate={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
      </div>

      <main className="lg:pl-64">
        <div className="mx-auto max-w-[1400px] p-4 sm:p-8">
          <Announcements />
          <Outlet />
        </div>
      </main>
    </div>
  );
}
